import type { Logger } from "@expert/logging";

import { setFullStoryUserProperties, trackFullStoryEvent } from "@expert/monitoring";
import { useEffect, useRef } from "react";

import { useAnalytics } from "@/analytics";

import { type RealtimeWebsocketProvider, useRealtimeWebsocketStore } from "./realtimeWebsocketStore";

interface ReportRealtimeWebsocketArgs {
    /** Whether the live connection is currently routed to Simplr (vs the default GAIA). */
    isLive: boolean;
    /** Partner of the active call, or `undefined` when idle. */
    partner: string | undefined;
    /** Resolved value of the `isSimplrWebsocketLiveEnabled` feature flag. */
    isSimplrWebsocketLiveEnabled: boolean;
    logger: Logger;
}

/**
 * Publishes which live realtime websocket (GAIA vs Simplr) an expert's active call is using, so the
 * `isSimplrWebsocketLiveEnabled` Simplr cutover can be observed end to end — the frontend looks
 * identical whichever provider serves Expert Assist. It:
 *
 * - drives {@link useRealtimeWebsocketStore} so the timeline can render a visible "Simplr" badge
 *   (captured in FullStory session replay),
 * - emits an `ExpertAssistSocketProviderChanged` business analytic (EDP) plus a matching FullStory custom event, and
 * - sets a `realtimeWebsocket` FullStory user property so pilot sessions are filterable.
 *
 * To stay out of the steady GAIA baseline for the ~all experts not in the pilot cohort, the analytic
 * and FullStory signals fire only around actual Simplr usage: the cut over to Simplr and the revert
 * back to GAIA. The store is always kept current so the badge shows and hides correctly.
 */
export function useReportRealtimeWebsocket({
    isLive,
    partner,
    isSimplrWebsocketLiveEnabled,
    logger,
}: ReportRealtimeWebsocketArgs) {
    const { dispatcher } = useAnalytics();
    const setProvider = useRealtimeWebsocketStore((state) => state.setProvider);
    const previousIsLiveRef = useRef<boolean | undefined>(undefined);

    useEffect(() => {
        const provider: RealtimeWebsocketProvider = isLive ? "simplr" : "gaia";
        setProvider(provider);

        logger.info(
            { module: "PartnerAwareWebsocketProvider", partner, isSimplrWebsocketLiveEnabled, isLive },
            `GAIA | using ${isLive ? "Simplr" : "GAIA"} websocket connection`,
        );

        const previousIsLive = previousIsLiveRef.current;
        previousIsLiveRef.current = isLive;

        // Report only around actual Simplr usage — the cutover to Simplr, or the revert back to GAIA.
        // This skips the steady GAIA baseline every non-pilot expert would otherwise emit per call.
        if (isLive || previousIsLive === true) {
            void dispatcher.dispatchBusinessEvent("ExpertAssistSocketProviderChanged", {
                provider,
                partner,
                isLive,
                isSimplrWebsocketLiveEnabled,
            });
            setFullStoryUserProperties({ realtimeWebsocket: provider });
            trackFullStoryEvent("ExpertAssistSocketProviderChanged", { provider, partner: partner ?? "unknown" });
        }
    }, [isLive, partner, isSimplrWebsocketLiveEnabled, logger, dispatcher, setProvider]);
}
